import React, { useEffect, useState } from "react";
import { unescape } from "lodash";
import { LazyLoadImage } from "react-lazy-load-image-component";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { getDisplayDuration, getLanguagesStringFromDamProduct } from "../../utils/Helper";
import BackArrow from "../common/BackArrow";
import AWSAPI from "../../utils/AWSAPI";
import commerceAPI from "../../utils/CommerceAPI";
import { sendProductEventClickGTM } from "../../utils/TagManagerHelper";
import PortalModal from "../common/PortalModal";
import LandingPopUpModal from "./LandingPopUpModal";

const HeroImageWrapper = styled.div`
  background: ${(props) => props.bgHeroColor};
`;

function LandingSessionHero({ sessionData }) {
  const {
    id,
    bgImage,
    title,
    bgColor,
    sessionSubtitle,
    language,
    duration,
    numberOfSessions,
    tag,
    isDark,
    isInsight,
    productNumber,
    price,
    // isHireATeamSession,
    isEvent,
    isVideoLibrarySession,
    isArea42Labs,
  } = sessionData;
  const location = useLocation();
  const previousLocation = location?.state?.previous;
  const backLink = previousLocation === "/" ? "/#trending-consulting-sessions" : "/";
  const [damLanguages, setDamLanguages] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isInsight) {
      commerceAPI
        .getDAMCustomFields(productNumber)
        .then((res) => {
          const damContent = unescape(res.data.productIframe);
          try {
            const jsonData = JSON.parse(damContent);
            setDamLanguages(getLanguagesStringFromDamProduct(jsonData));
          } catch (_error) {
            throw new Error("Incorrect Shopware Custom Field Content");
          }
        })
        .catch(() => {
          AWSAPI.getDAMContent(productNumber, "Document")
            .then((res) => {
              setDamLanguages(getLanguagesStringFromDamProduct(res.data));
            })
            .catch(() => {
              setDamLanguages(language);
            });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsight, productNumber, tag]);

  useEffect(() => {
    const product = {
      title,
      id,
      price,
      dataLayerLanguage: language,
      tag,
    };

    let from = "consulting";
    if (isEvent) {
      from = "events";
    } else if (isInsight) {
      from = "insights";
    } else if (isVideoLibrarySession) {
      from = "video-library";
    } else if (isArea42Labs) {
      from = "area-42-labs";
    }
    sendProductEventClickGTM(product, true, true, from);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ctaText = () => {
    if (isVideoLibrarySession) {
      return "Watch";
    }
    if (isInsight) {
      return "Download";
    }
    return "Book";
  };

  return (
    <div className="landing-sh light-gray-bg">
      <BackArrow isDark={!isDark} text="Go to Homepage" onPressBackLink={backLink} />
      <HeroImageWrapper className="sh-left" bgHeroColor={bgColor}>
        <LazyLoadImage className="hero-image" src={bgImage} alt={title} />
      </HeroImageWrapper>
      <div className="sh-right">
        <div className="right-head">
          <p className="label medium text-uppercase greytext">{tag}</p>
          <h3 className="medium-contrast">{title}</h3>
          <p
            className="light-contrast description-sh mb-0"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sessionSubtitle }}
          />
        </div>
        <div className={`right-body ${isVideoLibrarySession && "align-items-center"}`}>
          <div>
            <p className="light-contrast mb-0">
              <span className="medium-contrast">Language:</span> {damLanguages ?? language}
            </p>
            {!isInsight ? (
              !isEvent &&
              !isVideoLibrarySession && (
                <>
                  <p className="light-contrast mb-0">
                    <span className="medium-contrast">Duration:</span>{" "}
                    {numberOfSessions > 1 ? `${numberOfSessions} meetings x` : "1 meeting x"}{" "}
                    {getDisplayDuration(duration)}
                  </p>

                  <p className="light-contrast mb-0">
                    <span className="medium-contrast">Where: </span>Online, Microsoft Teams
                  </p>
                </>
              )
            ) : (
              <div className="document-wrap d-flex mt-0">
                <span className="medium-contrast">Typology:</span>&nbsp;
                <p className="mb-0">PDF document</p>
              </div>
            )}
          </div>

          <button type="button" className="btn btn-primary" onClick={() => setIsOpen(true)}>
            {ctaText()}
          </button>
          <PortalModal open={isOpen} onClose={() => setIsOpen(false)}>
            <LandingPopUpModal setShow={setIsOpen} />
          </PortalModal>
        </div>
      </div>
    </div>
  );
}

LandingSessionHero.propTypes = {
  sessionData: PropTypes.shape({
    id: PropTypes.node,
    bgImage: PropTypes.node,
    title: PropTypes.string,
    bgColor: PropTypes.string,
    sessionSubtitle: PropTypes.string,
    language: PropTypes.string,
    duration: PropTypes.node,
    numberOfSessions: PropTypes.node,
    tag: PropTypes.string,
    isDark: PropTypes.bool,
    isInsight: PropTypes.bool,
    productNumber: PropTypes.node,
    price: PropTypes.node,
    isHireATeamSession: PropTypes.bool,
    isEvent: PropTypes.bool,
    isVideoLibrarySession: PropTypes.bool,
    isArea42Labs: PropTypes.bool,
  }).isRequired,
};

export default LandingSessionHero;
